/**
 * PMT-06 — the deposit a quarterly (QRMP) client makes for the quarter's first two months
 * (FR-E16, B-10). Shown on a quarterly GSTR-3B.
 *
 * Two figures per month, both the service's: the self-assessment one from the month's own draft
 * and credit, and the fixed sum (35% of the previous quarter's cash) only where that cash is
 * known — otherwise the service's reason is printed instead of a number. Every rule here is a
 * placeholder until V-E4 clears it, and the card says so. Nothing is paid from this screen.
 */
import { Alert, Card } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { ApiError, type Pmt06Record, periods } from '../lib/api'
import { rupees } from '../lib/calendar'
import { monthsOf } from '../lib/periodWords'

export function pmt06Words(record: Pmt06Record): { selfAssessment: string; fixedSum: string } {
  const self = record.self_assessment
  const selfAssessment =
    self.state === 'computed'
      ? `Self-assessment: ₹${rupees(self.total_paise)} in cash.`
      : `Self-assessment: ₹${rupees(self.total_paise)} — ${self.says}`
  const fixed = record.fixed_sum
  const fixedSum =
    fixed['state'] === 'computed'
      ? `Fixed sum: ₹${rupees(Number(fixed['total_paise'] ?? 0))} — ${String(fixed['basis_note'] ?? '')}`
      : `Fixed sum: not worked out. ${String(fixed['reason'] ?? '')}`
  return { selfAssessment, fixedSum }
}

export function Pmt06Card({ clientId, quarter }: { clientId: string; quarter: string }) {
  const [rows, setRows] = useState<Pmt06Record[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setRows([])
    Promise.all(monthsOf(quarter).slice(0, 2).map((m) => periods.pmt06(clientId, m)))
      .then((bodies) => {
        if (live) setRows(bodies.map((b) => b.data))
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'PMT-06 could not be worked out.')
      })
    return () => {
      live = false
    }
  }, [clientId, quarter])

  return (
    <Card className="mt-6 p-4" data-testid="pmt06">
      <p className="gst-panel-h mb-2">PMT-06 deposits for the quarter&rsquo;s first two months</p>
      {error && <Alert tone="error">{error}</Alert>}
      <ul className="space-y-2">
        {rows.map((row) => {
          const words = pmt06Words(row)
          return (
            <li key={row.period} className="gst-meta">
              <strong>
                {row.period} — due {row.due_date}
              </strong>
              <br />
              {words.selfAssessment}
              <br />
              {words.fixedSum}
            </li>
          )
        })}
      </ul>
      <p className="gst-meta mt-2">
        The rules behind these figures and their dates are not yet verified (V-E4). ProGST does
        not record the deposit; it is credited in the cash ledger and used by the quarter&rsquo;s
        GSTR-3B.
      </p>
    </Card>
  )
}
